import React, { useState } from 'react';
import { Settings, Wifi, WifiOff } from 'lucide-react';

export interface QualityLevel {
  value: string;
  label: string;
  resolution: string;
  bitrate?: string;
}

interface QualitySelectorProps {
  selectedQuality: string;
  onQualityChange: (quality: string) => void;
  isYouTube?: boolean;
  connectionSpeed?: 'fast' | 'medium' | 'slow';
  className?: string;
}

const qualityLevels: QualityLevel[] = [
  { value: 'auto', label: 'Auto', resolution: 'Adaptive', bitrate: 'Smart' },
  { value: 'hd1080', label: '1080p', resolution: '1920×1080', bitrate: 'High' },
  { value: 'hd720', label: '720p', resolution: '1280×720', bitrate: 'High' },
  { value: 'medium', label: '480p', resolution: '854×480', bitrate: 'Medium' },
  { value: 'small', label: '360p', resolution: '640×360', bitrate: 'Low' },
  { value: 'tiny', label: '240p', resolution: '426×240', bitrate: 'Very Low' }
];

const QualitySelector: React.FC<QualitySelectorProps> = ({
  selectedQuality,
  onQualityChange,
  isYouTube = false,
  connectionSpeed = 'medium',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getConnectionIcon = () => {
    switch (connectionSpeed) {
      case 'fast':
        return <Wifi className="w-3 h-3 text-green-400" />;
      case 'medium':
        return <Wifi className="w-3 h-3 text-yellow-400" />;
      case 'slow':
        return <WifiOff className="w-3 h-3 text-red-400" />;
      default:
        return <Wifi className="w-3 h-3 text-gray-400" />;
    }
  };

  const getCurrentQualityLabel = () => {
    const quality = qualityLevels.find(q => q.value === selectedQuality);
    return quality?.label || 'Auto';
  };

  const getRecommendedQuality = () => {
    switch (connectionSpeed) {
      case 'fast':
        return 'hd1080';
      case 'medium':
        return 'hd720';
      case 'slow':
        return 'small';
      default:
        return 'auto';
    }
  };

  const handleQualitySelect = (quality: string) => {
    onQualityChange(quality);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-black/20 text-white rounded-lg hover:bg-black/30 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-black"
        aria-label="Video Quality Settings"
      >
        <Settings className="w-4 h-4" />
        <span className="text-sm font-medium">{getCurrentQualityLabel()}</span>
        {getConnectionIcon()}
        <svg 
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute bottom-full right-0 mb-2 bg-black/95 rounded-xl py-2 min-w-[200px] shadow-lg backdrop-blur-sm border border-white/10 transform origin-bottom-right z-50">
          {/* Header */}
          <div className="px-4 py-2 border-b border-white/10">
            <div className="flex items-center gap-2 text-white text-sm font-medium">
              <Settings className="w-4 h-4" />
              <span>Video Quality</span>
            </div>
            {isYouTube && (
              <p className="text-xs text-gray-400 mt-1">
                YouTube auto-adjusts based on connection
              </p>
            )}
          </div>

          {/* Quality Options */}
          <div className="py-1">
            {qualityLevels.map((quality) => {
              const isSelected = selectedQuality === quality.value;
              const isRecommended = quality.value === getRecommendedQuality();

              return (
                <button
                  key={quality.value}
                  onClick={() => handleQualitySelect(quality.value)}
                  className={`w-full px-4 py-2 text-left hover:bg-white/10 transition-colors flex items-center justify-between group ${
                    isSelected ? 'text-orange-400 bg-orange-500/10' : 'text-white'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{quality.label}</span>
                      {isRecommended && !isSelected && (
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">
                          Recommended
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 group-hover:text-gray-300">
                      {quality.resolution} • {quality.bitrate}
                    </div>
                  </div>

                  {isSelected && (
                    <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-white/10">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              {getConnectionIcon()}
              <span>
                Connection: {connectionSpeed.charAt(0).toUpperCase() + connectionSpeed.slice(1)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Overlay to close dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default QualitySelector;



